import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, Pencil, Trash2, Save, Check } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const fieldKey = (provider, field) => `${provider}:${field}`;

const AdminIntegrations = () => {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingProvider, setSavingProvider] = useState(null);
  const [savedProvider, setSavedProvider] = useState(null);

  // Estado local de edición -- nada de esto se manda al backend hasta que
  // se aprieta "Guardar" de la tarjeta correspondiente.
  const [enabledDraft, setEnabledDraft] = useState({}); // { [provider]: bool }
  const [openFields, setOpenFields] = useState(new Set()); // Set("provider:field") -- con input visible
  const [fieldValues, setFieldValues] = useState({}); // { "provider:field": string tipeado }
  const [clearedFields, setClearedFields] = useState(new Set()); // Set("provider:field") -- marcado para borrar

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/admin/integraciones");
      setIntegrations(data);
      setEnabledDraft(Object.fromEntries(data.map((i) => [i.provider, i.is_enabled])));
      setOpenFields(new Set());
      setFieldValues({});
      setClearedFields(new Set());
    } catch {
      setError("No se pudieron cargar las integraciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleEnabled = (provider) => {
    setEnabledDraft((prev) => ({ ...prev, [provider]: !prev[provider] }));
  };

  const openFieldEditor = (provider, field) => {
    const key = fieldKey(provider, field);
    setOpenFields((prev) => new Set(prev).add(key));
    setClearedFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const cancelFieldEditor = (provider, field) => {
    const key = fieldKey(provider, field);
    setOpenFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setFieldValues((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const markForClear = (provider, field) => {
    const key = fieldKey(provider, field);
    setClearedFields((prev) => new Set(prev).add(key));
    setOpenFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const undoClear = (provider, field) => {
    const key = fieldKey(provider, field);
    setClearedFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const setFieldValue = (provider, field, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldKey(provider, field)]: value }));
  };

  const saveProvider = async (integration) => {
    const provider = integration.provider;
    setSavingProvider(provider);
    setSavedProvider(null);
    setError("");

    const fields = {};
    for (const name of Object.keys(integration.fields)) {
      const key = fieldKey(provider, name);
      if (clearedFields.has(key)) {
        fields[name] = "";
      } else if (openFields.has(key) && fieldValues[key]) {
        fields[name] = fieldValues[key];
      }
    }

    const payload = { is_enabled: enabledDraft[provider] };
    if (Object.keys(fields).length > 0) payload.fields = fields;

    try {
      await api.put(`/api/admin/integraciones/${provider}`, payload);
      await load();
      setSavedProvider(provider);
      setTimeout(() => setSavedProvider(null), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar la integración.");
    } finally {
      setSavingProvider(null);
    }
  };

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-integrations">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Sistema</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Integraciones</h1>
        <p className="mt-2 text-sm text-[#A0A0A5] font-body">
          Claves y credenciales de servicios externos. Los valores nunca se muestran completos, ni siquiera acá.
        </p>

        {error && <p className="mt-4 text-xs text-red-400 font-body" data-testid="integrations-error">{error}</p>}
        {loading && <p className="mt-5 text-xs text-[#A0A0A5] font-body">Cargando…</p>}

        <div className="mt-6 grid grid-cols-1 gap-4">
          {!loading &&
            integrations.map((integration) => (
              <div
                key={integration.provider}
                className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6"
                data-testid={`integration-card-${integration.provider}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg border border-[#C9A227]/30 flex items-center justify-center shrink-0">
                      <Plug size={18} className="text-[#C9A227]" />
                    </div>
                    <div>
                      <h2 className="font-display font-bold text-lg uppercase tracking-wide">{integration.label}</h2>
                      {integration.actualizado_en && (
                        <p className="text-[10px] text-[#A0A0A5] font-body mt-0.5">
                          Última edición {new Date(integration.actualizado_en).toLocaleString("es-PE")}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleEnabled(integration.provider)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] uppercase tracking-wider font-display font-semibold transition ${
                      enabledDraft[integration.provider]
                        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                        : "bg-[#0D0E10] border-[#2E2F33] text-[#A0A0A5]"
                    }`}
                    data-testid={`toggle-enabled-${integration.provider}`}
                  >
                    <span
                      className={`w-8 h-4 rounded-full relative transition ${
                        enabledDraft[integration.provider] ? "bg-emerald-500" : "bg-[#2E2F33]"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-3 h-3 rounded-full bg-[#0D0E10] transition-all ${
                          enabledDraft[integration.provider] ? "left-4" : "left-0.5"
                        }`}
                      />
                    </span>
                    {enabledDraft[integration.provider] ? "Activo" : "Inactivo"}
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(integration.fields).map(([name, field]) => {
                    const key = fieldKey(integration.provider, name);
                    const isOpen = openFields.has(key);
                    const isCleared = clearedFields.has(key);

                    return (
                      <div key={name} className="bg-[#0D0E10] border border-[#2E2F33] rounded-lg p-4">
                        <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display">{field.label}</p>

                        {!isOpen ? (
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <span className="text-sm font-body text-[#F5F5F5]">
                              {isCleared ? (
                                <span className="text-red-300">Se borrará al guardar</span>
                              ) : field.configured ? (
                                `•••• ${field.last4 || ""}`
                              ) : (
                                <span className="text-[#A0A0A5]">No configurado</span>
                              )}
                            </span>
                            <div className="flex items-center gap-1 shrink-0">
                              {isCleared ? (
                                <button
                                  type="button"
                                  onClick={() => undoClear(integration.provider, name)}
                                  className="text-[10px] uppercase tracking-wider text-[#C9A227] font-display"
                                  data-testid={`undo-clear-${key}`}
                                >
                                  Deshacer
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openFieldEditor(integration.provider, name)}
                                    className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227]"
                                    title="Editar"
                                    data-testid={`edit-field-${key}`}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  {field.configured && (
                                    <button
                                      type="button"
                                      onClick={() => markForClear(integration.provider, name)}
                                      className="p-1.5 text-[#A0A0A5] hover:text-red-400"
                                      title="Borrar"
                                      data-testid={`clear-field-${key}`}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <input
                              type="text"
                              autoComplete="off"
                              value={fieldValues[key] || ""}
                              onChange={(e) => setFieldValue(integration.provider, name, e.target.value)}
                              placeholder="Nuevo valor"
                              className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                              data-testid={`field-input-${key}`}
                            />
                            <button
                              type="button"
                              onClick={() => cancelFieldEditor(integration.provider, name)}
                              className="mt-1.5 text-[10px] uppercase tracking-wider text-[#A0A0A5] hover:text-[#C9A227] font-display"
                              data-testid={`cancel-field-${key}`}
                            >
                              Cancelar
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => saveProvider(integration)}
                    disabled={savingProvider === integration.provider}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-50 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                    data-testid={`save-integration-${integration.provider}`}
                  >
                    <Save size={14} />
                    {savingProvider === integration.provider ? "Guardando…" : "Guardar"}
                  </button>
                  {savedProvider === integration.provider && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-300 font-body">
                      <Check size={14} /> Guardado
                    </span>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </PanelShell>
  );
};

export default AdminIntegrations;
